package com.evoapps.evoshift.domain.spreadsheet

import kotlinx.coroutines.await
import org.khronos.webgl.Int8Array
import org.khronos.webgl.Uint8Array
import kotlin.js.Promise

/**
 * The browser's DecompressionStream("deflate-raw") (Chrome 103+, Safari 16.4+, Firefox 113+, and
 * Node 18+ for the end-to-end suite), read chunk by chunk and cancelled one byte past
 * [maxOutput]; the plain-Kotlin [Inflate] where the browser has no such stream.
 */
actual suspend fun inflateRaw(data: ByteArray, maxOutput: Int): ByteArray {
    val stream = newDeflateRawStream() ?: return inflatePure(data, maxOutput)
    val writer = stream.writable.getWriter()
    val reader = stream.readable.getReader()
    val bytes = data.unsafeCast<Int8Array>()
    // A failure surfaces in read(); these promises are only kept from being reported as unhandled.
    (writer.write(Uint8Array(bytes.buffer, bytes.byteOffset, bytes.length)) as Promise<Any?>).catch { }
    (writer.close() as Promise<Any?>).catch { }
    val chunks = mutableListOf<Int8Array>()
    var size = 0
    try {
        while (true) {
            val result = (reader.read() as Promise<dynamic>).await()
            if (result.done == true) break
            val chunk = result.value.unsafeCast<Uint8Array>()
            size += chunk.length
            if (size > maxOutput) {
                (reader.cancel() as Promise<Any?>).catch { }
                throw inflatedTooLarge()
            }
            chunks += Int8Array(chunk.buffer, chunk.byteOffset, chunk.length)
        }
    } catch (e: SpreadsheetException) {
        throw e
    } catch (e: Throwable) {
        throw SpreadsheetException(SpreadsheetProblem.CORRUPT, e.message)
    }
    val out = Int8Array(size)
    var at = 0
    for (chunk in chunks) {
        out.set(chunk, at)
        at += chunk.length
    }
    return out.unsafeCast<ByteArray>()
}

/** A DecompressionStream for raw DEFLATE, or null where the platform has none or not this format. */
private fun newDeflateRawStream(): dynamic {
    val available = js("typeof DecompressionStream !== 'undefined'") as Boolean
    if (!available) return null
    return try {
        js("new DecompressionStream('deflate-raw')")
    } catch (e: Throwable) {
        null
    }
}
